import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import {
  DstFormCreateEditLookups,
  DstFormCreateEditService,
  DstFormLookupOption,
} from './dst-form-create-edit.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  buildHeaderDataShell,
} from '../../../shared/workspace-element-context';

export interface DstFormModel {
  FORMID: string;
  FORMNAME: string;
  FORMORDER: string;
  PRIMARY_TYPE: string;
  SECONDARY_TYPE: string;
  TERTIARY_TYPE: string;
  PRIMARYFORMTYPEDESC: string;
  PRIMARY_FORM_ACTIVE: string;
  SECONDARYFORMTYPEDESC: string;
  SECONDARY_FORM_ACTIVE: string;
  SECONDARY_FORM_ID: string;
  SIGNATURE_FLAG: string;
  ELECTION_FLAG: string;
  SECTION_NUMBER: string;
  DATACOLLECT_ACTION_FLAG: string;
  DATACOLLECT_ACTION_EXPL: string;
  ELECTION_TERM: string;
  ATTACHMENT_NAME: string;
  KEY_DIAGNOSTIC: string;
}

function defaultFormModel(): DstFormModel {
  return {
    FORMID: '',
    FORMNAME: '',
    FORMORDER: '',
    PRIMARY_TYPE: '',
    SECONDARY_TYPE: '',
    TERTIARY_TYPE: '',
    PRIMARYFORMTYPEDESC: ' ',
    PRIMARY_FORM_ACTIVE: ' ',
    SECONDARYFORMTYPEDESC: ' ',
    SECONDARY_FORM_ACTIVE: ' ',
    SECONDARY_FORM_ID: '',
    SIGNATURE_FLAG: ' ',
    ELECTION_FLAG: ' ',
    SECTION_NUMBER: '',
    DATACOLLECT_ACTION_FLAG: ' ',
    DATACOLLECT_ACTION_EXPL: '',
    ELECTION_TERM: '',
    ATTACHMENT_NAME: '',
    KEY_DIAGNOSTIC: '',
  };
}

function flagToSelectValue(raw: any): string {
  if (raw === 1 || raw === '1' || raw === 'Yes') return '1';
  if (raw === 0 || raw === '0' || raw === 'No') return '0';
  return raw != null && String(raw).trim() !== '' ? String(raw) : ' ';
}

@Component({
  selector: 'gtw-dst-form-create-edit',
  templateUrl: './dst-form-create-edit.component.html',
  styleUrls: ['./dst-form-create-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DstFormCreateEditComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  minimise = false;
  submitted = false;
  crudLoading = false;
  showFormLoader = true;

  filterParams: any = {};
  rowData: any = {};
  baseURLs: any = { admin: '', api: '', custom: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  modalTitle = 'Create';

  formModel: DstFormModel = defaultFormModel();

  lookups: DstFormCreateEditLookups | null = null;
  electionTypeOptions: DstFormLookupOption[] = [];
  dataCollectActionFlagOptions: DstFormLookupOption[] = [];

  private isInitialized = false;

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private dstFormCreateEditService: DstFormCreateEditService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  get isEdit(): boolean {
    return !!this.formModel.FORMID;
  }

  get formTypeLabel(): string {
    return this.isEdit ? 'Primary Form Type' : 'Form Type';
  }

  get formStatusLabel(): string {
    return this.isEdit ? 'Primary Form Status' : 'Form Status';
  }

  ngOnInit(): void {
    this.electionTypeOptions = this.dstFormCreateEditService.getElectionTypeOptions();
    this.dataCollectActionFlagOptions = this.dstFormCreateEditService.getDataCollectActionFlagOptions();
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      }),
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = this.GENERAL_CONFIG;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    const hdr = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = hdr.userSettingsData;
    this.cdr.markForCheck();
    this.tryInit();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalService || !this.GENERAL_CONFIG) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.subs.add(
      this.dstFormCreateEditService.getLookups().subscribe({
        next: (lookups) => {
          this.lookups = lookups;
          this.mapRowToForm();
          this.showFormLoader = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.lookups = {
            formTypes: [],
            yesNo: [],
            formStatuses: [],
          };
          this.mapRowToForm();
          this.showFormLoader = false;
          this.cdr.detectChanges();
        },
      }),
    );
  }

  private mapRowToForm(): void {
    const source = this.rowData || {};
    const hasFormId = source.FORMID != null && String(source.FORMID).trim() !== '';

    if (!hasFormId) {
      this.formModel = defaultFormModel();
      this.modalTitle = 'Create';
      return;
    }

    this.formModel = {
      ...defaultFormModel(),
      FORMID: String(source.FORMID ?? ''),
      FORMNAME: source.FORMNAME ?? '',
      FORMORDER: source.FORMORDER != null ? String(source.FORMORDER) : '',
      PRIMARY_TYPE: source.PRIMARY_TYPE ?? '',
      SECONDARY_TYPE: source.SECONDARY_TYPE ?? '',
      TERTIARY_TYPE: source.TERTIARY_TYPE ?? '',
      PRIMARYFORMTYPEDESC: source.PRIMARYFORMTYPEDESC?.trim() ? source.PRIMARYFORMTYPEDESC : ' ',
      PRIMARY_FORM_ACTIVE: flagToSelectValue(source.PRIMARY_FORM_ACTIVE),
      SECONDARYFORMTYPEDESC: source.SECONDARYFORMTYPEDESC?.trim() ? source.SECONDARYFORMTYPEDESC : ' ',
      SECONDARY_FORM_ACTIVE: flagToSelectValue(source.SECONDARY_FORM_ACTIVE),
      SECONDARY_FORM_ID: source.SECONDARY_FORM_ID != null ? String(source.SECONDARY_FORM_ID) : '',
      SIGNATURE_FLAG: flagToSelectValue(source.SIGNATURE_FLAG),
      ELECTION_FLAG: flagToSelectValue(source.ELECTION_FLAG),
      SECTION_NUMBER: source.SECTION_NUMBER ?? '',
      DATACOLLECT_ACTION_FLAG: flagToSelectValue(source.DATACOLLECT_ACTION_FLAG),
      DATACOLLECT_ACTION_EXPL: source.DATACOLLECT_ACTION_EXPL ?? '',
      ELECTION_TERM: source.ELECTION_TERM ?? '',
      ATTACHMENT_NAME: source.ATTACHMENT_NAME ?? '',
      KEY_DIAGNOSTIC: source.KEY_DIAGNOSTIC ?? '',
    };
    this.modalTitle = 'Edit';
  }

  private isBlankSelect(value: string): boolean {
    return value == null || String(value).trim() === '';
  }

  private validate(): boolean {
    const m = this.formModel;
    const errors: string[] = [];

    if (!m.FORMNAME?.trim()) errors.push('Form Name');
    if (this.isBlankSelect(m.PRIMARYFORMTYPEDESC)) errors.push(this.formTypeLabel);
    if (this.isBlankSelect(m.SIGNATURE_FLAG)) errors.push('Signature Type');
    if (this.isBlankSelect(m.ELECTION_FLAG)) errors.push('Election Type');
    if (this.isBlankSelect(m.PRIMARY_FORM_ACTIVE)) errors.push(this.formStatusLabel);
    if (!m.ATTACHMENT_NAME?.trim()) errors.push('Attachment Name');
    if (!m.PRIMARY_TYPE?.trim()) errors.push('Primary Type');
    if (!m.SECONDARY_TYPE?.trim()) errors.push('Secondary Type');
    if (!m.TERTIARY_TYPE?.trim()) errors.push('Tertiary Type');

    if (this.isEdit) {
      if (!m.FORMORDER?.trim()) errors.push('Form Order');
      if (this.isBlankSelect(m.SECONDARYFORMTYPEDESC)) errors.push('Secondary Form Type');
      if (this.isBlankSelect(m.SECONDARY_FORM_ACTIVE)) errors.push('Secondary Form Status');
    }

    if (errors.length) {
      this.gtwSnackbarService.openSnackBar(
        `Please complete required fields: ${errors.join(', ')}.`,
        'gtw-snackbar--red',
      );
      return false;
    }
    return true;
  }

  save(): void {
    if (this.crudLoading) return;
    this.submitted = true;
    if (!this.validate()) {
      this.cdr.markForCheck();
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG?.base_url || '';
    const clobSettings = {
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.rowData?.JCD_KEY || this.filterParams?.jcd_key,
    };
    const ssoId = this.USER_SETTINGS?.user?.sso_id || this.globalService?.sso_id || '';
    const m = this.formModel;

    const request$ = this.isEdit
      ? this.dstFormCreateEditService.update(
          baseUrl,
          {
            FORMID: m.FORMID,
            FORMNAME: m.FORMNAME,
            FORMORDER: m.FORMORDER,
            PRIMARY_TYPE: m.PRIMARY_TYPE,
            SECONDARY_TYPE: m.SECONDARY_TYPE,
            TERTIARY_TYPE: m.TERTIARY_TYPE,
            PRIMARYFORMTYPEDESC: m.PRIMARYFORMTYPEDESC.trim(),
            PRIMARY_FORM_ACTIVE: m.PRIMARY_FORM_ACTIVE,
            SECONDARYFORMTYPEDESC: m.SECONDARYFORMTYPEDESC.trim(),
            SECONDARY_FORM_ACTIVE: m.SECONDARY_FORM_ACTIVE,
            SECONDARY_FORM_ID: m.SECONDARY_FORM_ID,
            SIGNATURE_FLAG: m.SIGNATURE_FLAG,
            ELECTION_FLAG: m.ELECTION_FLAG,
            SECTION_NUMBER: m.SECTION_NUMBER,
            DATACOLLECT_ACTION_FLAG: m.DATACOLLECT_ACTION_FLAG,
            DATACOLLECT_ACTION_EXPL: m.DATACOLLECT_ACTION_EXPL,
            ELECTION_TERM: m.ELECTION_TERM,
            ATTACHMENT_NAME: m.ATTACHMENT_NAME,
            KEY_DIAGNOSTIC: m.KEY_DIAGNOSTIC,
          },
          clobSettings,
          this.globalService,
          this.filterParams,
          ssoId,
        )
      : this.dstFormCreateEditService.create(
          baseUrl,
          {
            FORMNAME: m.FORMNAME,
            PRIMARY_TYPE: m.PRIMARY_TYPE,
            SECONDARY_TYPE: m.SECONDARY_TYPE,
            TERTIARY_TYPE: m.TERTIARY_TYPE,
            PRIMARYFORMTYPEDESC: m.PRIMARYFORMTYPEDESC.trim(),
            PRIMARY_FORM_ACTIVE: m.PRIMARY_FORM_ACTIVE,
            SIGNATURE_FLAG: m.SIGNATURE_FLAG,
            ELECTION_FLAG: m.ELECTION_FLAG,
            SECTION_NUMBER: m.SECTION_NUMBER,
            DATACOLLECT_ACTION_FLAG: m.DATACOLLECT_ACTION_FLAG,
            DATACOLLECT_ACTION_EXPL: m.DATACOLLECT_ACTION_EXPL,
            ELECTION_TERM: m.ELECTION_TERM,
            ATTACHMENT_NAME: m.ATTACHMENT_NAME,
            KEY_DIAGNOSTIC: m.KEY_DIAGNOSTIC,
          },
          clobSettings,
          this.globalService,
          this.filterParams,
          ssoId,
        );

    this.subs.add(
      request$.subscribe({
        next: (res: any) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Save failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar(
            'Something went wrong while saving your changes, please try again.',
            'gtw-snackbar--red',
          );
          this.cdr.detectChanges();
        },
      }),
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
